import { useState, type JSX } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { apiFetch } from '../lib/api-client';
import { getSupabaseClient } from '../lib/auth';

/**
 * Full-screen gate shown when the logged-in user's Supabase Auth profile has
 * app_metadata.force_password_reset === true (set by a superadmin, or for a
 * first-time onboarding admin). Blocks the app until the user chooses a new
 * password. On success the API sets the password and clears the flag, then we
 * sign in fresh with the new credentials to get a clean session — the gate then
 * unmounts and the app renders normally.
 */
export function ForcePasswordResetGate(): JSX.Element {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }

    setSubmitting(true);
    setError(null);
    try {
      const supabase = await getSupabaseClient();
      // Capture the email from the current (magic-link) session BEFORE changing
      // the password — the password change revokes this session's refresh token.
      const { data: sessionData } = await supabase.auth.getSession();
      const email = sessionData.session?.user?.email ?? null;

      const res = await apiFetch('/api/v1/admin/companies/complete-password-reset', {
        method: 'POST',
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }

      // The password change revoked the magic-link session's refresh token, so
      // refreshSession() would 400 and sign the user out. Instead sign in fresh
      // with the credentials we just set — the server already cleared the flag,
      // so the new session is clean and SIGNED_IN unmounts this gate.
      if (email) {
        const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
        if (signInErr) throw new Error(signInErr.message);
      } else {
        // No email on the session (shouldn't happen) — fall back to a refresh.
        await supabase.auth.refreshSession();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to set password');
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 420, width: '100%' }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>Set a new password</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          For security, you must choose a new password before continuing.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          label="New Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth size="small"
          autoComplete="new-password"
          helperText="At least 8 characters"
          disabled={submitting}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Confirm Password"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          fullWidth size="small"
          autoComplete="new-password"
          disabled={submitting}
          sx={{ mb: 3 }}
        />

        <Button
          variant="contained"
          fullWidth
          onClick={() => void submit()}
          disabled={submitting || password.length < 8 || password !== confirm}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Saving…' : 'Set Password & Continue'}
        </Button>
      </Paper>
    </Box>
  );
}
